/**
 * <shipping-progress>
 *
 * Free shipping progress bar. Server-rendered by snippets/shipping-progress.liquid, then
 * hydrated and kept in sync entirely client-side. Nothing in assets/cart.js is modified;
 * this component only consumes events that theme code already dispatches on `document`:
 *
 *   - 'ajaxProduct:added'  assets/component-product-form.js. Carries no usable payload,
 *                          so we re-read the cart.
 *   - 'cart:update'        assets/cart.js. detail is the /cart/change.js response and
 *                          carries total_price + item_count, so no fetch is needed. This
 *                          is what keeps the PDP bar in sync when the customer changes
 *                          quantities in the cart drawer opened on top of the PDP.
 *
 * Currency: data-threshold and cart.total_price are both shop-currency cents, so the
 * ratio is rate-invariant and is computed on the raw values. Only the displayed amount is
 * converted, by Woolman.Utils.formatMoney, which applies parseFloat(Shopify.currency.rate)
 * internally. Never pre-multiply by the rate here or it is applied twice.
 */
(function () {
  if (customElements.get('shipping-progress')) return;

  // Shared across instances so N bars on one page make one request.
  let inFlight = null;

  function fetchCart() {
    if (inFlight) return inFlight;

    const base = (window.routes && window.routes.cart_url) || '/cart';

    inFlight = fetch(`${base}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then((res) => res.json())
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  }

  customElements.define('shipping-progress', class ShippingProgress extends HTMLElement {
    connectedCallback() {
      this.messageEl = this.querySelector('[data-shipping-progress-message]');
      this.statusEl = this.querySelector('[data-shipping-progress-status]');
      this.barEl = this.querySelector('[data-shipping-progress-bar]');
      this.fillEl = this.querySelector('[data-shipping-progress-fill]');

      this.threshold = parseInt(this.dataset.threshold, 10);
      if (!this.threshold || this.threshold <= 0) return;

      this.hideWhenEmpty = this.dataset.hideWhenEmpty === 'true';
      this.lastAnnounced = this.statusEl ? this.statusEl.textContent.trim() : '';

      this.onCartChange = this.onCartChange.bind(this);
      this.onPageShow = this.onPageShow.bind(this);

      document.addEventListener('ajaxProduct:added', this.onCartChange);
      document.addEventListener('cart:update', this.onCartChange);
      window.addEventListener('pageshow', this.onPageShow);

      // The Liquid-rendered total above may be stale (full page cache). Always re-read.
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('ajaxProduct:added', this.onCartChange);
      document.removeEventListener('cart:update', this.onCartChange);
      window.removeEventListener('pageshow', this.onPageShow);
      clearTimeout(this.announceTimer);
    }

    onPageShow(event) {
      if (event.persisted) this.refresh();
    }

    onCartChange(event) {
      const detail = event && event.detail;

      // Some dispatchers attach an unawaited res.json() Promise as detail, so type-check
      // hard rather than trusting the shape.
      if (detail && Number.isInteger(detail.total_price)) {
        this.render(detail.total_price, detail.item_count);
        return;
      }

      this.refresh();
    }

    refresh() {
      return fetchCart()
        .then((cart) => this.render(cart.total_price, cart.item_count))
        .catch((error) => {
          // Leave the server-rendered state in place rather than showing nonsense.
          console.error('[shipping-progress] Could not read the cart.', error);
        });
    }

    render(totalPrice, itemCount) {
      const total = Math.max(0, parseInt(totalPrice, 10) || 0);
      const remaining = Math.max(0, this.threshold - total);
      const percent = Math.min(100, Math.floor((total / this.threshold) * 100));
      const unlocked = remaining === 0;

      const strings = window.cartStrings || {};
      const message = unlocked
        ? strings.shippingFree || ''
        : (strings.shippingNotFree || '').replace('[amount]', window.Woolman.Utils.formatMoney(remaining));

      this.fillEl.style.setProperty('--shipping-progress', `${percent}%`);
      this.barEl.setAttribute('aria-valuenow', String(percent));
      this.barEl.setAttribute('aria-valuetext', message);
      this.classList.toggle('shipping-progress--unlocked', unlocked);
      this.messageEl.textContent = message;

      if (this.hideWhenEmpty && typeof itemCount === 'number') {
        this.toggleAttribute('hidden', itemCount === 0);
      }

      this.announce(message);
    }

    /**
     * Trailing-debounced, and only when the sentence changed, so a burst of quantity
     * clicks yields at most one announcement and a +1/-1 round trip yields none.
     */
    announce(message) {
      if (!this.statusEl || message === this.lastAnnounced) return;

      this.lastAnnounced = message;
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        this.statusEl.textContent = message;
      }, 500);
    }
  });
})();
